import React, { useRef, useState } from 'react';
import {
  Box, Typography, TextField, FormControl, InputLabel, Select, MenuItem,
  Alert, Paper, Tabs, Tab, Button, LinearProgress,
} from '@mui/material';
import { CloudUpload, UploadFile, Link as LinkIcon } from '@mui/icons-material';
import { contentAPI } from '../../services/api';

interface Props {
  embedUrl: string;
  description: string;
  interactiveType: string;
  onChange: (data: { embed_url: string; description: string; interactive_type: string }) => void;
}

function getUploadedFileType(url: string): 'image' | 'audio' | 'pdf' | 'other' {
  const lower = url.toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/.test(lower)) return 'image';
  if (/\.(mp3|wav|ogg|aac|flac)(\?|$)/.test(lower)) return 'audio';
  if (/\.pdf(\?|$)/.test(lower)) return 'pdf';
  return 'other';
}

function isUploadedFile(url: string): boolean {
  return url.startsWith('/uploads/') || url.includes('/uploads/');
}

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8080/api').replace(/\/api$/, '');

function toAbsoluteUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_ORIGIN}${url}`;
}

const ACCEPTED_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
  'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/aac', 'audio/flac',
  'application/pdf',
].join(',');

const MultimediaContentEditor: React.FC<Props> = ({ embedUrl, description, interactiveType, onChange }) => {
  const initialTab = isUploadedFile(embedUrl) ? 1 : 0;
  const [tab, setTab] = useState(initialTab);
  const [uploadedUrl, setUploadedUrl] = useState(isUploadedFile(embedUrl) ? embedUrl : '');
  const [embedInput, setEmbedInput] = useState(isUploadedFile(embedUrl) ? '' : embedUrl);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const update = (patch: Partial<{ embed_url: string; description: string; interactive_type: string }>) => {
    onChange({ embed_url: embedUrl, description, interactive_type: interactiveType, ...patch });
  };

  const handleEmbedChange = (val: string) => {
    setEmbedInput(val);
    update({ embed_url: val });
  };

  const handleFile = async (file: File) => {
    const allowed = ['image/', 'audio/', 'application/pdf'];
    if (!allowed.some(t => file.type.startsWith(t))) {
      setUploadError('Unsupported file type. Please upload an image, audio file, or PDF.');
      return;
    }
    setUploadError(null);
    setUploading(true);
    setUploadProgress(0);
    try {
      const res = await contentAPI.uploadFile(file, pct => setUploadProgress(pct));
      const fileUrl = res.data.url;
      setUploadedUrl(fileUrl);
      update({ embed_url: fileUrl, interactive_type: 'upload' });
    } catch {
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const fileType = getUploadedFileType(uploadedUrl);
  const absoluteUrl = toAbsoluteUrl(uploadedUrl);

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Embed URL" icon={<LinkIcon fontSize="small" />} iconPosition="start" />
        <Tab label="Upload File" icon={<UploadFile fontSize="small" />} iconPosition="start" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Embed interactive tools like H5P, CodeSandbox, Figma, Google Slides, Kahoot, Miro, or any iframe-compatible content.
          </Alert>
          <Box display="flex" gap={2} sx={{ mb: 2 }}>
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Content Type</InputLabel>
              <Select
                value={interactiveType}
                label="Content Type"
                onChange={e => update({ interactive_type: e.target.value })}
              >
                <MenuItem value="embed">Generic Embed</MenuItem>
                <MenuItem value="h5p">H5P Interactive</MenuItem>
                <MenuItem value="codesandbox">CodeSandbox</MenuItem>
                <MenuItem value="figma">Figma</MenuItem>
                <MenuItem value="slides">Google Slides</MenuItem>
                <MenuItem value="miro">Miro Board</MenuItem>
                <MenuItem value="kahoot">Kahoot</MenuItem>
                <MenuItem value="scorm">SCORM Content</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Embed URL"
              placeholder="https://codesandbox.io/embed/... or any embeddable URL"
              value={embedInput}
              onChange={e => handleEmbedChange(e.target.value)}
            />
          </Box>
          <TextField
            fullWidth
            label="Description / Instructions"
            multiline
            rows={3}
            value={description}
            onChange={e => update({ description: e.target.value })}
            sx={{ mb: 2 }}
            placeholder="Describe what learners should do with this interactive content..."
          />
          {embedInput && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Preview</Typography>
              <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1 }}>
                <Box sx={{ position: 'relative', paddingTop: '60%' }}>
                  <iframe
                    src={embedInput}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media"
                    title="Multimedia preview"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                  />
                </Box>
              </Paper>
            </Box>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Upload images (JPG, PNG, GIF, WebP), audio files (MP3, WAV, OGG), or PDFs.
          </Alert>

          {/* Drop zone */}
          <Paper
            variant="outlined"
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            sx={{
              p: 5, mb: 2, textAlign: 'center', cursor: 'pointer',
              borderStyle: 'dashed', borderWidth: 2,
              bgcolor: dragOver ? 'action.hover' : 'background.default',
              borderColor: dragOver ? 'primary.main' : 'divider',
              transition: 'all 0.2s',
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Drag & drop a file here
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Images · Audio · PDF — up to 500 MB
            </Typography>
            <Button variant="outlined" size="small" startIcon={<UploadFile />}>
              Choose File
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              style={{ display: 'none' }}
              onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
            />
          </Paper>

          {uploading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Uploading… {uploadProgress}%
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {uploadError && <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>}

          <TextField
            fullWidth
            label="Description / Instructions"
            multiline
            rows={3}
            value={description}
            onChange={e => update({ description: e.target.value })}
            sx={{ mb: 2 }}
            placeholder="Describe what learners should do with this content..."
          />

          {uploadedUrl && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Preview</Typography>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                {fileType === 'image' && (
                  <Box textAlign="center">
                    <img
                      src={absoluteUrl}
                      alt="Uploaded"
                      style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 4 }}
                    />
                  </Box>
                )}
                {fileType === 'audio' && (
                  <Box>
                    <audio controls style={{ width: '100%' }}>
                      <source src={absoluteUrl} />
                      Your browser does not support the audio element.
                    </audio>
                  </Box>
                )}
                {fileType === 'pdf' && (
                  <Box>
                    <embed
                      src={absoluteUrl}
                      type="application/pdf"
                      width="100%"
                      height="500px"
                      style={{ border: 'none', display: 'block' }}
                    />
                    <Box textAlign="center" mt={1}>
                      <Button href={absoluteUrl} target="_blank" size="small" variant="outlined">
                        Open PDF in new tab
                      </Button>
                    </Box>
                  </Box>
                )}
                {fileType === 'other' && (
                  <Alert severity="success">
                    File uploaded: <strong>{uploadedUrl.split('/').pop()}</strong>
                  </Alert>
                )}
              </Paper>
              {fileType !== 'other' && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  File uploaded successfully.
                </Alert>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MultimediaContentEditor;
